import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { Mic, Sparkles, Box, FileDown } from 'lucide-react';

/**
 * Platform Section — shows the 4-step end-to-end workflow.
 * Lives under id="about" to match the Header's Platform nav link.
 * Swiss-minimalist design.
 */
export default function AboutSection() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';

  const steps = [
    {
      num: '01',
      icon: Mic,
      title: isTurkish ? 'Sesli / Metin Komut' : 'Voice / Text Command',
      desc: isTurkish ? 'Fikrinizi yazarak veya mikrofonla açıklayın; dil kapsamı ve doğruluk girdiye göre değişebilir.' : 'Describe your idea by typing or speaking; language coverage and accuracy can vary by input.',
    },
    {
      num: '02',
      icon: Sparkles,
      title: isTurkish ? '2D Konsept Render' : '2D Concept Render',
      desc: isTurkish ? 'Yapılandırılmış görsel sağlayıcı, girdiden incelenecek bir konsept taslağı üretir; süre ve kalite proje ile servis durumuna göre değişir.' : 'A configured image provider drafts a review-required concept visual; latency and quality vary with the project and service availability.',
    },
    {
      num: '03',
      icon: Box,
      title: isTurkish ? '3D Prototip' : '3D Prototype',
      desc: isTurkish ? 'Meshy (beta) görsel 3D model (GLB/OBJ) üretir — işlem süresi değişkendir; üretilebilir parametrik CAD değildir.' : 'Meshy (beta) generates a visual 3D model (GLB/OBJ) — processing time varies; not manufacturable parametric CAD.',
    },
    {
      num: '04',
      icon: FileDown,
      title: isTurkish ? 'CAD + Dokümantasyon' : 'CAD + Documentation',
      desc: isTurkish ? 'Doğrulanmış geometri ve yapılandırılmış bağlantı varsa Onshape aktarımı; ön BOM ve maliyet analizi inceleme gerektirir.' : 'Onshape export when verified geometry and a configured connection are available; preliminary BOM and cost analysis require review.',
    },
  ];

  return (
    <section id="about" className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden" data-testid="platform-section">
      <div className="pointer-events-none absolute top-0 right-1/3 w-[500px] h-[500px] bg-[#FFB547] opacity-[0.04] blur-[140px] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-2xl"
        >
          <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/60 mb-6">
            {isTurkish ? 'Platform' : 'The Platform'}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1] mb-4">
            {isTurkish ? (<>Dört adımda <span className="font-bold">incelemeye hazır konsepte</span></>) : (<>From idea to a review-ready concept <span className="font-bold">in four steps</span></>)}
          </h2>
          <p className="text-white/60 max-w-xl leading-relaxed">
            {isTurkish
              ? 'Ön endüstriyel tasarım iş akışı tek çalışma alanında; sonuçlar üretimden önce mühendislik doğrulaması gerektirir.'
              : 'A preliminary industrial-design workflow in one workspace; outputs require engineering validation before production.'}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-[#0A0A0A] p-8 hover:bg-[#0D0D0D] transition-colors relative"
                data-testid={`platform-step-${step.num}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:border-white/40 transition-colors">
                    <Icon className="w-4 h-4 text-white/80" />
                  </div>
                  <span className="text-xs font-mono tracking-widest text-white/60">{step.num}</span>
                </div>
                <h3 className="text-base font-medium text-white mb-2">{step.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
